import { WidgetApi } from '@/api/widgetApi'
import { WidgetExtended } from '@/types/dashboard'

/* -------------------------------------
 * DOCX Export Configuration
 * ----------------------------------- */
const DOCX_EXPORT_OPTIONS = {
    documentType: 'document' as const,
    style: {
        titleSize: 32,
        headingSpacing: 240,
        paragraphSpacing: 240,
        lineSpacing: 1.15,
        heading1Size: 32,
        heading2Size: 28,
        heading3Size: 24,
        paragraphSize: 24,
        listItemSize: 24
    }
}

/* -------------------------------------
 * Filename Generator
 * ----------------------------------- */
function generateExportFilename (widget: WidgetExtended, extension: 'docx' | 'md'): string {
    const widgetName = widget.Title || 'AI_Summary'
    const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    return `${widgetName}_${timestamp}.${extension}`
}

/* -------------------------------------
 * Export to DOCX Function
 * ----------------------------------- */
export async function exportMarkdownToDocx (
    markdownContent: string,
    widget: WidgetExtended
): Promise<void> {
    const { convertMarkdownToDocx, downloadDocx } = await import('@mohtasham/md-to-docx')
    const blob = await convertMarkdownToDocx(markdownContent, DOCX_EXPORT_OPTIONS)
    const filename = generateExportFilename(widget, 'docx')
    downloadDocx(blob, filename)
}

/* -------------------------------------
 * Download Markdown Function
 * ----------------------------------- */
export function downloadMarkdownFile (
    markdownContent: string,
    widget: WidgetExtended
): void {
    const blob = new Blob([ markdownContent ], { type: 'text/markdown;charset=utf-8' })
    const filename = generateExportFilename(widget, 'md')

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

/* -------------------------------------
 * Regenerate AI Summary Function
 * ----------------------------------- */
export async function regenerateAiSummary (
    widgetId: number,
    runDataFetch: (force?: boolean) => Promise<void>
): Promise<void> {
    await WidgetApi.forceUpdate(widgetId, false)
    await runDataFetch(true) // Manually trigger immediate data fetch
}
